import { Row, Col, Card, Typography, Empty } from 'antd';
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { variantTokens } from '../../theme/theme';
import { animated, to } from '@react-spring/web';
import { useTooltip } from '@nivo/tooltip';
import { useMemo, createElement, useCallback } from 'react';

const { Title, Text } = Typography;

const PRIMARY = variantTokens.primary;

const SECONDARY = variantTokens.secondary;

const ChartsConsumption = ({ chartData, barChartFill, pieChartFill, tooltipStyle, defs }) => {

    // Custom bar component with only top corners rounded
    const TopRoundedBar = (props) => {
        const {
            bar: { data, ...bar },
            style: {
                borderColor,
                color,
                height,
                labelColor,
                labelOpacity,
                labelX,
                labelY,
                transform,
                width,
            },
            borderRadius,
            borderWidth,
            label,
            shouldRenderLabel,
            isInteractive,
            onClick,
            onMouseEnter,
            onMouseLeave,
            tooltip,
            isFocusable,
            ariaLabel,
            ariaLabelledBy,
            ariaDescribedBy,
        } = props;

        const { theme } = props;
        const { showTooltipFromEvent, showTooltipAt, hideTooltip } = useTooltip();

        const renderTooltip = useMemo(
            () => () => createElement(tooltip, { ...bar, ...data }),
            [tooltip, bar, data]
        );
        const handleClick = useCallback(
            (event) => onClick && onClick({ color: bar.color, ...data }, event),
            [bar, data, onClick]
        );
        const handleTooltip = useCallback(
            (event) => showTooltipFromEvent(renderTooltip(), event),
            [showTooltipFromEvent, renderTooltip]
        );
        const handleMouseEnter = useCallback(
            (event) => {
                onMouseEnter && onMouseEnter(data, event);
                showTooltipFromEvent(renderTooltip(), event);
            },
            [data, onMouseEnter, showTooltipFromEvent, renderTooltip]
        );
        const handleMouseLeave = useCallback(
            (event) => {
                onMouseLeave && onMouseLeave(data, event);
                hideTooltip();
            },
            [data, hideTooltip, onMouseLeave]
        );
        const handleFocus = useCallback(
            () =>
                showTooltipAt(renderTooltip(), [
                    bar.absX + bar.width / 2,
                    bar.absY,
                ]),
            [showTooltipAt, renderTooltip, bar]
        );
        const handleBlur = useCallback(() => hideTooltip(), [hideTooltip]);

        return (
            <animated.g transform={transform}>
                <defs>
                    <clipPath id={`round-corner-${label}`}>
                        <animated.rect
                            x={0}
                            y={0}
                            rx={borderRadius}
                            ry={borderRadius}
                            width={to(width, (v) => Math.max(v, 0))}
                            height={to(height, (v) => Math.max(v + borderRadius, 0))}
                        />
                    </clipPath>
                </defs>
                <animated.rect
                    clipPath={`url(#round-corner-${label})`}
                    width={to(width, (v) => Math.max(v, 0))}
                    height={to(height, (v) => Math.max(v, 0))}
                    fill={data.fill ?? color}
                    strokeWidth={borderWidth}
                    stroke={borderColor}
                    focusable={isFocusable}
                    tabIndex={isFocusable ? 0 : undefined}
                    aria-label={ariaLabel ? ariaLabel(data) : undefined}
                    aria-labelledby={ariaLabelledBy ? ariaLabelledBy(data) : undefined}
                    aria-describedby={ariaDescribedBy ? ariaDescribedBy(data) : undefined}
                    onMouseEnter={
                        isInteractive ? handleMouseEnter : undefined
                    }
                    onMouseMove={isInteractive ? handleTooltip : undefined}
                    onMouseLeave={
                        isInteractive ? handleMouseLeave : undefined
                    }
                    onClick={isInteractive ? handleClick : undefined}
                    onFocus={
                        isInteractive && isFocusable ? handleFocus : undefined
                    }
                    onBlur={
                        isInteractive && isFocusable ? handleBlur : undefined
                    }
                />
                {shouldRenderLabel && (
                    <animated.text
                        x={labelX}
                        y={labelY}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fillOpacity={labelOpacity}
                        style={{
                            ...theme.labels.text,
                            pointerEvents: 'none',
                            fill: labelColor,
                        }}
                    >
                        {label}
                    </animated.text>
                )}
            </animated.g>
        );
    };

    return (
        <Row gutter={[16, 16]} justify="space-between">
            {(!chartData || (!chartData.monthlyChart && !chartData.limitChart)) ? (
                <Col span={24}>
                    <Card
                        styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)' } }}
                    >
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No consumption data available" />
                    </Card>
                </Col>
            ) : (
                <>
                    <Col xs={24} md={16}>
                        <Card
                            styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)', height: 'clamp(18.75rem, 50vh, 25rem)' } }}
                            style={{ height: 'clamp(18.75rem, 50vh, 25rem)' }}
                        >
                            <div style={{ height: '100%' }}>
                                {chartData && chartData.monthlyChart && chartData.monthlyChart.length > 0 ? (
                                    <ResponsiveBar
                                        data={chartData.monthlyChart}
                                        keys={['bandwidth']}
                                        indexBy="month"
                                        margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                                        padding={0.7}
                                        innerPadding={4}
                                        minValue={0}
                                        maxValue={chartData.monthlyChart.length > 0 ? Math.max(...chartData.monthlyChart.map(d => d.bandwidth)) * 1.2 : 0}
                                        groupMode="grouped"
                                        layout="vertical"
                                        valueScale={{ type: 'linear' }}
                                        indexScale={{
                                            type: 'band',
                                            round: true,
                                            padding: 0.5
                                        }}
                                        enableGridY={true}
                                        gridYValues={(() => {
                                            const maxValue = chartData.monthlyChart.length > 0 ? Math.max(...chartData.monthlyChart.map(d => d.bandwidth)) * 1.2 : 0;
                                            const intervals = [];
                                            for (let i = 0; i <= maxValue; i += 100) {
                                                intervals.push(i);
                                            }
                                            return intervals;
                                        })()}
                                        borderRadius={8}
                                        barComponent={TopRoundedBar}
                                        borderWidth={1}
                                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                        defs={defs}
                                        fill={barChartFill}
                                        colors={[PRIMARY]}
                                        enableLabel={false}
                                        maxBarSize={20}
                                        axisBottom={{
                                            tickSize: 5, tickPadding: 5, tickRotation: -45
                                        }}
                                        axisLeft={{
                                            tickSize: 5, tickPadding: 5, tickRotation: 0,
                                            legend: 'GB', legendPosition: 'middle', legendOffset: -40,
                                            tickValues: (() => {
                                                const maxValue = chartData.monthlyChart.length > 0 ? Math.max(...chartData.monthlyChart.map(d => d.bandwidth)) * 1.2 : 0;
                                                const intervals = [];
                                                for (let i = 0; i <= maxValue; i += 100) {
                                                    intervals.push(i);
                                                }
                                                return intervals;
                                            })()
                                        }}
                                        tooltip={({ data, value, color, indexValue }) => (
                                            <div style={tooltipStyle}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <div style={{
                                                        width: '0.75rem',
                                                        height: '0.75rem',
                                                        backgroundColor: color,
                                                        marginRight: '0.5rem'
                                                    }} />
                                                    <span style={{ fontWeight: 'bold' }}>{indexValue}:</span>
                                                    <span style={{ marginLeft: '0.25rem' }}>{value.toFixed(2)} GB</span>
                                                </div>
                                            </div>
                                        )}
                                        motionConfig="gentle"
                                        animate={true}
                                    />
                                ) : (
                                    <Empty description="No monthly data available" />
                                )}
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card
                            styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)', height: 'clamp(18.75rem, 50vh, 25rem)' } }}
                            style={{ height: 'clamp(18.75rem, 50vh, 25rem)' }}
                        >
                            {chartData && chartData.limitChart && chartData.consumptionLimit > 0 ? (
                                <div style={{ height: '100%', position: 'relative' }}>
                                    <ResponsivePie
                                        data={chartData.limitChart}
                                        margin={{ top: 20, right: 20, bottom: 60, left: 20 }}
                                        innerRadius={0.85}
                                        padAngle={1}
                                        cornerRadius={8}
                                        activeOuterRadiusOffset={8}
                                        animate={true}
                                        startAngle={-150}
                                        endAngle={150}
                                        motionConfig="gentle"
                                        enableArcLabels={false}
                                        borderWidth={1}
                                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                        defs={defs}
                                        fill={pieChartFill}
                                        colors={({ id }) => {
                                            if (id === 'Consumed') return PRIMARY;
                                            if (id === 'Remaining') return SECONDARY;
                                            return '#cccccc'; // Colore di fallback
                                        }}
                                        enableSliceLabels={false}
                                        tooltip={({ datum }) => (
                                            <div style={tooltipStyle}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <div style={{
                                                        width: '0.75rem',
                                                        height: '0.75rem',
                                                        backgroundColor: datum.color,
                                                        marginRight: '0.5rem'
                                                    }} />
                                                    <span style={{ fontWeight: 'bold' }}>{datum.id}:</span>
                                                    <span style={{ marginLeft: '0.25rem' }}>{datum.value.toFixed(2)} GB</span>
                                                </div>
                                            </div>
                                        )}
                                        legends={[
                                            {
                                                anchor: 'bottom',
                                                direction: 'row',
                                                translateY: 56,
                                                itemWidth: 80,
                                                itemHeight: 18,
                                                itemTextColor: '#333',
                                                symbolSize: 12,
                                                symbolShape: 'square',
                                                effects: [
                                                    {
                                                        on: 'hover',
                                                        style: {
                                                            itemTextColor: '#000'
                                                        }
                                                    }
                                                ]
                                            }
                                        ]}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -60%)',
                                        textAlign: 'center',
                                        pointerEvents: 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        gap: '0rem',
                                        height: '40%'
                                    }}>
                                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', lineHeight: '1' }}>
                                            {chartData.totalConsumed.toFixed(0)}
                                        </div>
                                        <div style={{ fontSize: '1rem', color: '#666', marginTop: '-0.25rem' }}>
                                            GB USED
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: '1rem'
                                }}>
                                    {chartData?.consumptionLimit === 0 ? (
                                        <>
                                            <Title level={2} style={{ color: PRIMARY, margin: 0 }}>
                                                ∞
                                            </Title>
                                            <Text type="secondary">Unlimited consumption</Text>
                                        </>
                                    ) : (
                                        <Empty description="No limit data available" />
                                    )}
                                </div>
                            )}
                        </Card>
                    </Col>
                </>
            )}
        </Row>
    );
};

export default ChartsConsumption;